import React from "react";
//Limitless imports
import { useNotificationContext } from "@limitless/common/hooks/notification/NotificationContext";
import { fetchUserTasks } from "@limitless/portal/apis/UserInboxActions";
import { log as pilog } from "@limitless/common/utils";
import { recordCustomEvent } from "@limitless/analytics/hooks/AnalyticsHelper";
import { INITIAL_TASKLIST_FILTERS_STATE } from "./ExpertTaskListFilterHook";
import { calculateFilteredMessages } from "./TaskListHelper";
import { useExpertAnnouncementsContext } from "../expert-announcements/ExpertAnnouncementsContext";
import { useExpertOnboardingContext } from "../expert-onboarding/ExpertOnboardingContext";
import { useExpertProfileContext } from "../../hooks/expert-profile/ExpertProfileContext";
import { useExpertSupportConversationHistoryContext } from "../expert-support-conversation-history/ExpertSupportConversationHistoryContext";
import { useAppConfig } from "@limitless/common/hooks";
const INITIAL_TASKLIST_STATE = {
  loading: false,
  filteredMessages: {},
  messages: {},
  isPassedTasks: false,
  activeFilterCount: 0,
  unreadStickyCount: 0,
  stickyMessageCount: 0,
  blacklist: [],
  count: {},
  lastTaskListFetch: null
};
export const useTaskList = () => {
  var _getFeatureSwitches;
  const log = pilog.getLogger("useTaskList");
  // Use State to keep the values
  const [tasklist, setTaskList] = React.useState(INITIAL_TASKLIST_STATE);
  const [tasklistCallbacks, setTasklistCallbacks] = React.useState([]);
  //manage task list filters
  const [tasklistFilters, setTasklistFilters] = React.useState(INITIAL_TASKLIST_FILTERS_STATE);
  const {
    loadUserAnnouncements
  } = useExpertAnnouncementsContext();
  const {
    loadInactivatedMemberships,
    profile
  } = useExpertProfileContext();
  const {
    getExpertOnboarding,
    getOnboardingSummary
  } = useExpertOnboardingContext();
  const {
    addFailureNotification
  } = useNotificationContext();
  const {
    fetchConversationHistory
  } = useExpertSupportConversationHistoryContext();
  const {
    getFeatureSwitches,
    getTasksStickyThreshold
  } = useAppConfig();
  const {
    expertLLMSupport
  } = (_getFeatureSwitches = getFeatureSwitches()) !== null && _getFeatureSwitches !== void 0 ? _getFeatureSwitches : false;
  const getStickyTaskListThreshold = getTasksStickyThreshold();
  const isStickyThresholdExceeded = tasklist.stickyMessageCount >= getStickyTaskListThreshold;
  const getUserInbox = (fetchPass, minMsgFee = 0, maxMsgFee = 0, newFilters, refreshCall = false, isRequireAdditionalCall = false) => {
    setTaskList({
      ...tasklist,
      loading: true
    });
    fetchUserTasks({
      fetchPass,
      minMsgFee,
      maxMsgFee,
      newFilters,
      refreshCall
    }, (inbox, count) => {
      if (!inbox.hasError) {
        var _Object$keys$length, _Object$keys;
        const inboxData = {};
        Object.keys(inbox).map(msgId => {
          if (tasklist.blacklist.includes(msgId)) {
            return;
          }
          inboxData[msgId] = {
            ...inbox[msgId],
            displayName: inbox[msgId]["groupName"]
          };
        });
        const {
          filteredMessages,
          activeFilterCount,
          unreadStickyCount,
          stickyMessageCount
        } = calculateFilteredMessages(inboxData, newFilters || tasklistFilters);
        //blacklist on remains for 1 API call
        setTaskList({
          loading: false,
          filteredMessages,
          messages: inboxData,
          isPassedTasks: fetchPass,
          activeFilterCount,
          unreadStickyCount,
          stickyMessageCount,
          blacklist: [],
          count,
          lastTaskListFetch: new Date().getTime()
        });
        if (isRequireAdditionalCall) {
          //every time we get the user inbox we may aswell get the announcements too
          // this doesn't exist for CM

          if (fetchConversationHistory && profile.email && expertLLMSupport && process.env.REACT_APP_NAME !== "ExpertMobileApp") {
            fetchConversationHistory(profile.email);
          }
          if (loadUserAnnouncements) {
            loadUserAnnouncements();
          } else {
            loadInactivatedMemberships();
          }
          if (getExpertOnboarding) {
            getExpertOnboarding();
          }
          if (getOnboardingSummary) {
            getOnboardingSummary();
          }
        }
        recordCustomEvent({
          eventLabel: "Inbox Size",
          eventName: "inbox_size",
          eventValue: (_Object$keys$length = (_Object$keys = Object.keys(inboxData)) === null || _Object$keys === void 0 ? void 0 : _Object$keys.length) !== null && _Object$keys$length !== void 0 ? _Object$keys$length : 0
        });
      } else {
        addFailureNotification("Failed to retrieve tasks. Please try again.");
        setTaskList(INITIAL_TASKLIST_STATE);
      }
      if (tasklistCallbacks.length) {
        try {
          tasklistCallbacks.map(tlCB => tlCB());
        } catch (error) {
          log.warn("Failed to invoke tasklist callback %o", error);
        }
      }
    });
  };
  const applyUIOnlyFilters = () => {
    const {
      filteredMessages,
      activeFilterCount,
      unreadStickyCount
    } = calculateFilteredMessages(tasklist.messages, tasklistFilters);
    setTaskList({
      ...tasklist,
      loading: false,
      filteredMessages,
      activeFilterCount,
      unreadStickyCount
    });
  };
  const fetchTaskList = (isRequireAdditionalCall = false) => {
    getUserInbox(false, tasklistFilters.taskFee.minFee, tasklistFilters.taskFee.maxFee, tasklistFilters, false, isRequireAdditionalCall);
  };
  const resetFiltersAndFetchTaskList = () => {
    getUserInbox(tasklistFilters.passedTasks.pass.value, INITIAL_TASKLIST_FILTERS_STATE);
  };
  const fetchPassedTaskList = () => {
    getUserInbox(true, tasklistFilters.taskFee.minFee, tasklistFilters.taskFee.maxFee, tasklistFilters, false, false);
  };
  const resetFilters = () => {
    log.debug("Restting filters");
    setTaskList({
      ...tasklist,
      filteredMessages: tasklist.messages,
      isPassedTasks: false,
      activeFilterCount: 0
    });
  };
  const applyFilters = () => {
    log.debug("Applying TaskList Filters");
    getUserInbox(tasklistFilters.passed, tasklistFilters.taskFee.minFee, tasklistFilters.taskFee.maxFee, tasklistFilters);
  };
  const applyFiltersOnRefresh = () => {
    log.debug("Applying TaskList Filters on Refresh");
    getUserInbox(tasklistFilters.passedTasks.pass.value, tasklistFilters.taskFee.minFee, tasklistFilters.taskFee.maxFee, tasklistFilters, true);
  };
  const updateTaskListRecord = newMessageInbox => {
    log.debug(newMessageInbox);
    //only update a record if it already exists
    if (tasklist.messages[newMessageInbox.messageId]) {
      //add is the same as update
      const existingMessageInbox = tasklist.messages[newMessageInbox.messageId];
      addTaskListRecord({
        ...existingMessageInbox,
        ...newMessageInbox
      });
    }
  };
  const addTaskListRecord = messageInbox => {
    let messages = {};

    //if it's a new sticky message, pop it at the top
    if ((messageInbox === null || messageInbox === void 0 ? void 0 : messageInbox.type) === "respondSticky" && (!tasklist.messages[messageInbox.messageId] || tasklist.messages[messageInbox.messageId].type === "respondNew" || tasklist.messages[messageInbox.messageId].type === "respond" || tasklist.messages[messageInbox.messageId].type === "respondExisting")) {
      var _tasklist$messages$me;
      messages[messageInbox.messageId] = messageInbox;

      // Update the tasklist with the new manually generated sticky message
      if (((_tasklist$messages$me = tasklist.messages[messageInbox.messageId]) === null || _tasklist$messages$me === void 0 ? void 0 : _tasklist$messages$me.messageId) === messageInbox.messageId) {
        tasklist.messages[messageInbox.messageId] = messageInbox;
      }
      messages = {
        ...messages,
        ...tasklist.messages
      };
    } else {
      var _tasklist$messages$me2, _tasklist$messages$me3;
      // Remove expiry sticky messages from the top of list
      if (((_tasklist$messages$me2 = tasklist.messages[messageInbox.messageId]) === null || _tasklist$messages$me2 === void 0 ? void 0 : _tasklist$messages$me2.messageId) === messageInbox.messageId && ((_tasklist$messages$me3 = tasklist.messages[messageInbox.messageId]) === null || _tasklist$messages$me3 === void 0 ? void 0 : _tasklist$messages$me3.type) === "respondSticky" && (messageInbox.type === "respondNew" || messageInbox.type === "respond")) {
        delete tasklist.messages[messageInbox.messageId];
      }
      messages = {
        ...tasklist.messages
      };
      messages[messageInbox.messageId] = messageInbox;
    }
    log.debug(messages);
    log.debug(tasklist);
    const isStickyThresholdReached = isStickyThresholdExceeded && messageInbox.type === "respondSticky";
    const {
      filteredMessages,
      unreadStickyCount,
      stickyMessageCount
    } = calculateFilteredMessages(messages, tasklistFilters, isStickyThresholdReached, profile);
    setTaskList({
      ...tasklist,
      filteredMessages,
      messages,
      unreadStickyCount,
      stickyMessageCount
    });
  };
  const removeTasklistRecord = (messageId, blacklistMessage = false) => {
    const messages = {
      ...tasklist.messages
    };
    Reflect.deleteProperty(messages, messageId);
    const blacklist = blacklistMessage ? [messageId] : [];
    const {
      filteredMessages,
      unreadStickyCount,
      stickyMessageCount
    } = calculateFilteredMessages(messages, tasklistFilters);
    setTaskList({
      ...tasklist,
      filteredMessages,
      messages,
      unreadStickyCount,
      blacklist,
      stickyMessageCount
    });
  };
  const removeTaskListRecordAndBlackList = messageId => {
    removeTasklistRecord(messageId, true);
  };
  const clearTasklist = () => {
    setTaskList(INITIAL_TASKLIST_STATE);
    setTasklistFilters(INITIAL_TASKLIST_FILTERS_STATE);
  };
  const addTasklistCallback = cb => {
    if (typeof cb === "function") {
      const newCBList = tasklistCallbacks.slice();
      newCBList.push(cb);
      setTasklistCallbacks(newCBList);
    }
  };

  // Make the context object:
  return {
    tasklist,
    taskCount: Object.keys(tasklist === null || tasklist === void 0 ? void 0 : tasklist.filteredMessages).length,
    fetchTaskList,
    fetchPassedTaskList,
    applyFilters,
    applyFiltersOnRefresh,
    tasklistFilters,
    setTasklistFilters,
    resetFilters,
    addTaskListRecord,
    updateTaskListRecord,
    removeTasklistRecord,
    resetFiltersAndFetchTaskList,
    removeTaskListRecordAndBlackList,
    clearTasklist,
    addTasklistCallback
  };
};